import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  BodySmall,
  TitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import SignInPage from 'src/components/authentication/signin/sign-in/sign-in';
import PhoneRegistration from 'src/components/authentication/signin/phone-registration/phone-registration';
import AuthenticatorAndSmsRegister from './authenticator-app-registration/authenticator-and-sms-register';
import AuthenticatorAppRegistration from './authenticator-app-registration/authenticator-app-registration';
import AuthenticatorOtpVerification from './authenticator-app-registration/authenticator-otp-verification';
import AuthenticatorAndSmsLogin from './authenticator-app-registration/authenticator-and-sms-login';
import AuthenticatorAppReset from './authenticator-app-registration/authenticator-app-reset';
import OtpPage from 'src/components/authentication/signin/otp-page/otp-page';
import PhoneResetConfirmation from 'src/components/authentication/signin/phone-reset-confirmation/phone-reset-confirmation';
import DynamicAgency from 'src/components/authentication/signin/dynamic-agency/dynamic-agency';
import SSOLogin from 'src/components/authentication/signin/sso/sso';

import {AuthFlow} from 'src/components/authentication/auth-flow';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import Logo from 'src/images/sense-logo.svg';
import SenseCircleImage from 'src/images/sso-login-circle-image.svg';
import {classify} from 'src/utils/classify';
import {
  performSignIn,
  checkRedirection,
} from 'src/action-creators/authentication';

import {
  selectUserPhoneNumber,
  selectCurrentAgency,
  selectMfaAuthChannel,
  selectShouldEnrollTotp,
} from 'src/selectors/authentication';

import {
  selectAgencyData,
  selectAgencyConfig,
  selectAgencyBrandingSettingsLogo,
} from 'src/selectors/agency';

import ArrowLeft from 'src/images/icons/chevron-left.svg?noAttrs';

import type {SignInContainerProps, Credentials} from 'src/types/authentication';

import css from './signin-container.css';
import maincss from 'src/components/authentication/main.css';


const SignInContainer = (props: SignInContainerProps) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const {dynamicAgency} = props;

  const centreAlignedPages = [
    AuthFlow.SMS_REGISTRATION.RESET_PHONE,
    AuthFlow.TOTP.RESET_DEVICE,
  ];

  const IgnoreBackBtn = [
    AuthFlow.SMS_REGISTRATION.RESET_PHONE,
    AuthFlow.TOTP.RESET_DEVICE,
    AuthFlow.TOTP.SIGN_IN,
  ];

  const userPhone = useSelector(selectUserPhoneNumber);
  const agency = useSelector(selectCurrentAgency);
  const userAuthChannel = useSelector(selectMfaAuthChannel);
  // @ts-expect-error - TS2339 - Property 'ssoAuthUrl' does not exist on type 'Agency'.
  const ssoAuthUrl = useSelector(selectAgencyData).ssoAuthUrl;
  // @ts-expect-error - TS2339 - Property 'showDualAuthForm' does not exist on type 'Agency'.
  const showDualAuthForm = useSelector(selectAgencyData).showDualAuthForm;
  const agencyLogo = useSelector(selectAgencyBrandingSettingsLogo);
  const authenticatorEnrollTotp = useSelector(selectShouldEnrollTotp);

  const [curPage, setCurPage] = React.useState('');
  // flow can be set according to the api response
  const [mfaFlow, setMfaFlow] = React.useState<string>('');
  const [pageHistory, setPageHistory] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [creds, setCreds] = React.useState<Credentials>({
    email: '',
    password: '',
  });
  const [isCentreAligned, setIsCentreAligned] = React.useState(false);

  const {isExtension} = useEnvironmentContext();

  const isRedirect = () => {
    // redirect=true should always be last
    const queryParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    if (queryParams.redirect) {
      return true;
    }

    if (!queryParams.nextSearch) {
      return;
    }

    const params = Object.fromEntries(
      new URLSearchParams(queryParams.nextSearch),
    );
    return params.redirect;
  };

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const isFromSignUp = () => {
    const queryParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    if (queryParams.fromSignUp) {
      return true;
    }
  };

  const setAuthFlow = (
    agency: any,
    authChannel: any,
    userPhone: any,
    shouldEnrollTotp: any,
  ) => {
    if (!agency.mfa_auth_channel) {
      dispatch(performSignIn(agency));
      return;
    }

    if (authChannel === 'SMS' && userPhone) {
      goToPage(AuthFlow.SMS.OTP_INPUT, 'SMS');
    } else if (authChannel === 'SMS') {
      goToPage(
        AuthFlow.SMS_REGISTRATION.PHONE_REGISTRATION,
        'SMS_REGISTRATION',
      );
    } else if (authChannel === 'TOTP' && shouldEnrollTotp) {
      goToPage(AuthFlow.TOTP.TOTP_AND_SMS_REGISTRATION_SELECTION, 'TOTP');
    } else if (!authChannel || authChannel === 'TOTP') {
      goToPage(AuthFlow.TOTP.TOTP_VERIFY, 'TOTP');
    }
  };

  const handleRedirection = () => {
    setIsLoading(true);
    dispatch(checkRedirection())
      .then((response) => {
        if (!response) {
          return;
        }
        setAuthFlow(
          //@ts-expect-error
          response.agency,
          //@ts-expect-error
          response?.mfa_settings?.auth_channel,
          //@ts-expect-error
          response?.mfa_settings?.phone_number,
          //@ts-expect-error
          response?.mfa_settings?.should_enroll_totp,
        );
      })
      .catch((error) => {
        console.log('error while checking redirection ', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSignUp = () => {
    setIsLoading(true);
    try {
      setAuthFlow(agency, userAuthChannel, userPhone, authenticatorEnrollTotp);
    } catch {
      console.log('error while setting auth flow from signup');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (dynamicAgency) {
      return;
    }

    if (isRedirect()) {
      handleRedirection();
    } else if (isFromSignUp()) {
      handleSignUp();
    }
  }, []);

  const goToPage = (page: string, newMfaFlow?: string | null) => {
    if (!mfaFlow && !newMfaFlow) {
      return;
    }

    if (newMfaFlow) {
      setMfaFlow(newMfaFlow);
    }

    let flow;
    if (mfaFlow) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly SMS_REGISTRATION: { readonly SIGN_IN: "SIGN_IN"; readonly PHONE_REGISTRATION: "PHONE_REGISTRATION"; readonly OTP_INPUT: "OTP_INPUT"; readonly RESET_PHONE: "RESET_PHONE"; }; readonly SMS: { ...; }; readonly TOTP: { ...; }; readonly EMAIL: { ...; }; readonly DYNAMIC_AGENCY: { ...; }; }'.
      flow = AuthFlow[mfaFlow];
    } else if (newMfaFlow) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly SMS_REGISTRATION: { readonly SIGN_IN: "SIGN_IN"; readonly PHONE_REGISTRATION: "PHONE_REGISTRATION"; readonly OTP_INPUT: "OTP_INPUT"; readonly RESET_PHONE: "RESET_PHONE"; }; readonly SMS: { ...; }; readonly TOTP: { ...; }; readonly EMAIL: { ...; }; readonly DYNAMIC_AGENCY: { ...; }; }'.
      flow = AuthFlow[newMfaFlow];
    } else {
      return;
    }

    const nextPage = flow[page];

    const curPageHistory = [...pageHistory];
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
    curPageHistory.push(curPage);

    if (centreAlignedPages.includes(nextPage)) {
      setIsCentreAligned(true);
    } else {
      setIsCentreAligned(false);
    }

    if (IgnoreBackBtn.includes(nextPage)) {
      setPageHistory([]);
    } else {
      setPageHistory(curPageHistory);
    }

    setCurPage(nextPage);
  };

  const goToPrevPage = () => {
    if (!pageHistory.length) {
      return;
    }
    const curPageHistory = [...pageHistory];
    const prevPage = curPageHistory.pop();
    // @ts-expect-error - TS2345 - Argument of type 'undefined' is not assignable to parameter of type 'SetStateAction<string>'.
    setCurPage(prevPage);
    setPageHistory(curPageHistory);
    setMfaFlow('');
  };

  const getContent = () => {
    if (!mfaFlow && ssoAuthUrl) {
      if (showDualAuthForm) {
        return (
          <div className={css.ssoWithEmailContainer}>
            <SignInPage
              goToPage={goToPage}
              dynamicAgency={dynamicAgency}
              setCreds={setCreds}
            />
            <div
              className={classify(css.divider, {
                [css.extensionDivider]: isExtension,
              })}
            >
              <hr className={css.sectionDivider} />
              <BodySmall>{t('OR', 'OR')}</BodySmall>
              <hr className={css.sectionDivider} />
            </div>
            <SSOLogin ssoAuthUrl={ssoAuthUrl} showDualAuthForm />
          </div>
        );
      }
      return (
        <div className={css.ssoOnlyContainer}>
          {!isExtension && <SenseCircleImage />}
          {agencyLogo ? (
            <img src={agencyLogo} className={css.ssoOnlyLogo} />
          ) : (
            <Logo />
          )}
          <TitleMedium>
            {t('Hi_Welcome_to_Sense', 'Hi, Welcome to Sense!')}
          </TitleMedium>
          <SSOLogin ssoAuthUrl={ssoAuthUrl} />
        </div>
      );
    }

    switch (mfaFlow) {
      case 'TOTP':
        switch (curPage) {
          case AuthFlow.TOTP.TOTP_AND_SMS_SELECTION:
            return <AuthenticatorAndSmsLogin goToPage={goToPage} />;
          case AuthFlow.TOTP.TOTP_VERIFY:
            return <AuthenticatorOtpVerification goToPage={goToPage} />;
          case AuthFlow.TOTP.RESET_DEVICE:
            return <AuthenticatorAppReset />;
          case AuthFlow.TOTP.TOTP_REGISTRATION:
            return <AuthenticatorAppRegistration goToPage={goToPage} />;
          case AuthFlow.TOTP.PHONE_REGISTRATION:
            return <PhoneRegistration goToPage={goToPage} hideSkip={false} />;
          case AuthFlow.TOTP.TOTP_AND_SMS_REGISTRATION_SELECTION:
            return <AuthenticatorAndSmsRegister goToPage={goToPage} />;
          case AuthFlow.TOTP.OTP_INPUT:
            return <OtpPage goToPage={goToPage} />;
          case AuthFlow.SMS_REGISTRATION.RESET_PHONE:
            return <PhoneResetConfirmation />;
          default:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
        }
      case 'SMS_REGISTRATION':
        switch (curPage) {
          case AuthFlow.SMS_REGISTRATION.SIGN_IN:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
          case AuthFlow.SMS_REGISTRATION.PHONE_REGISTRATION:
            return <PhoneRegistration goToPage={goToPage} hideSkip={true} />;
          case AuthFlow.SMS_REGISTRATION.OTP_INPUT:
            return <OtpPage goToPage={goToPage} />;
          case AuthFlow.SMS_REGISTRATION.RESET_PHONE:
            return <PhoneResetConfirmation />;
          default:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
        }
      case 'SMS':
        switch (curPage) {
          case AuthFlow.SMS_REGISTRATION.SIGN_IN:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
          case AuthFlow.SMS_REGISTRATION.OTP_INPUT:
            return <OtpPage goToPage={goToPage} />;
          case AuthFlow.SMS_REGISTRATION.RESET_PHONE:
            return <PhoneResetConfirmation />;
          default:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
        }
      case 'EMAIL':
        switch (curPage) {
          case AuthFlow.EMAIL.SIGN_IN:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
          case AuthFlow.EMAIL.OTP_INPUT:
            return <OtpPage goToPage={goToPage} />;
          default:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
        }
      case 'DYNAMIC_AGENCY':
        switch (curPage) {
          case AuthFlow.DYNAMIC_AGENCY.SIGN_IN:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
          case AuthFlow.DYNAMIC_AGENCY.SELECT_AGENCY:
            return <DynamicAgency creds={creds} />;
          default:
            return (
              <SignInPage
                goToPage={goToPage}
                dynamicAgency={dynamicAgency}
                setCreds={setCreds}
              />
            );
        }
      default:
        return (
          <SignInPage
            goToPage={goToPage}
            dynamicAgency={dynamicAgency}
            setCreds={setCreds}
          />
        );
    }
  };

  return isLoading ? (
    <div className={css.loader}>
      <CircularLoader colorToken="colorFillPrimary" size="large" />
    </div>
  ) : (
    <div className={maincss.contentSideBar}>
      {pageHistory.length !== 0 && (
        <>
          {isExtension ? (
            <div className={maincss.senseLogoWithBtn}>
              <Button
                ariaLabel={t('back', 'back')}
                iconRightName="angle-left"
                onClick={goToPrevPage}
                size="medium"
                type="tertiary"
              />
              <Logo />
            </div>
          ) : (
            <Button
              iconLeftName="angle-left"
              onClick={goToPrevPage}
              size="medium"
              type="tertiary"
            >
              {getTranslatedText(t, CONSTANTS.BACK_BUTTON_TEXT)}
            </Button>
          )}
        </>
      )}

      <div
        className={classify(
          isCentreAligned || ssoAuthUrl
            ? maincss.containerContentCentreAligned
            : maincss.containerContent,
          {
            [maincss.containerContentExt]: isExtension,
            [maincss.containerContentTopAligned]:
              ssoAuthUrl && !showDualAuthForm && !isExtension,
          },
        )}
      >
        {getContent()}
      </div>
    </div>
  );
};

export default SignInContainer;
